import { useHistory } from "react-router-dom";
import { t } from "@/i18n-js/instance";
import { usePunditUserContext } from "@circle-react/contexts";
import { useHelpWidgetContext } from "../context/HelpWidgetContext";
import { useRevealingMessageStore } from "../stores/useRevealingMessageStore";
import type { VirtualConversationType } from "../types/VirtualMessage";

const TRANSLATION_KEY_MAP = {
  chat: {
    userKey: "ask_question_initial_message",
    botKeys: ["ask_question_initial_bot_response"],
  },
  feedback: {
    userKey: "give_feedback_initial_message",
    botKeys: ["give_feedback_initial_bot_response"],
  },
  report_bug: {
    userKey: "report_bug_initial_message",
    botKeys: ["report_bug_initial_bot_response"],
  },
  copilot_action: {
    userKey: "perform_an_action_initial_message",
    botKeys: [
      "perform_an_action_initial_bot_response_1",
      "perform_an_action_initial_bot_response_2",
    ],
  },
} as const;

export const useVirtualConversation = () => {
  const history = useHistory();
  const { currentCommunityMember } = usePunditUserContext();
  const {
    addVirtualMessage,
    clearVirtualMessages,
    setVirtualConversationType,
  } = useHelpWidgetContext();
  const { clearMessages } = useRevealingMessageStore();

  const createVirtualMessage = (
    content: string,
    authorType: "user" | "bot" = "user",
  ) => ({
    id: `virtual-${performance.now()}`,
    content,
    author_name:
      authorType === "user" ? currentCommunityMember?.name || "User" : "Circle",
    author_type: authorType,
    channel: "circle_bot" as const,
    created_at: new Date().toISOString(),
    show_fading_text_effect: authorType === "bot" && !!content.trim(),
  });

  const getInitialMessage = (type: VirtualConversationType): string => {
    const translationKeys = TRANSLATION_KEY_MAP[type] || "";
    return t(`help_widget.chat_bot.templates.${translationKeys.userKey}`);
  };

  const getBotResponse = (type: VirtualConversationType): string[] => {
    const translationKeys = TRANSLATION_KEY_MAP[type] || "";
    const botResponses: string[] = [];
    for (const key of translationKeys.botKeys) {
      botResponses.push(t(`help_widget.chat_bot.templates.${key}`));
    }
    return botResponses;
  };

  const startVirtualConversation = (type: VirtualConversationType) => {
    clearVirtualMessages();
    clearMessages();

    const initialMessage = getInitialMessage(type);

    setVirtualConversationType(type);
    addVirtualMessage(createVirtualMessage(initialMessage, "user"));

    history.push("/conversations/virtual");

    const botResponses = getBotResponse(type);
    setTimeout(() => {
      addVirtualMessage(createVirtualMessage(botResponses[0], "bot"));
    }, 2000);

    if (botResponses.length > 1) {
      setTimeout(() => {
        addVirtualMessage(createVirtualMessage(botResponses[1], "bot"));
      }, 4000);
    }
  };

  return { startVirtualConversation };
};
